import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaUser, 
  FaTag,
  FaShieldAlt,
  FaCode,
  FaLightbulb,
  FaRocket,
  FaBug,
  FaEye,
  FaShare,
  FaTwitter,
  FaLinkedin,
  FaCopy,
  FaCog,
  FaClock,
  FaChevronRight,
  FaCheck,
  FaBookmark
} from "react-icons/fa";
import Logo from "../components/Logo";
import Footer from "../components/Footer";
import "../styles/BlogPost.css";

// Sample blog posts data - same as in Blog.jsx
const blogPosts = [
  {
    id: 1,
    title: "Revolutionizing Malware Analysis: How Triagz Empowers Security Researchers with AI-Driven Investigation",
    excerpt: "Explore how artificial intelligence is revolutionizing malware analysis and how triagz leverages LLM to provide unprecedented insights into threat detection and analysis.",
    content: `
      <h2>Introduction</h2>
      <p>Malware analysis is a critical component of cybersecurity defense, but traditional approaches often require researchers to juggle multiple tools, command-line interfaces, and complex scripts. Triagz revolutionizes this process by combining the power of DeepSeek LLM with natural language processing, allowing researchers to conduct comprehensive malware analysis through simple conversational queries.</p>
      
      <h2>The Power of Natural Language Analysis</h2>
      <p>With Triagz, malware researchers can focus on investigation and analysis rather than remembering complex command syntax. Simply describe what you want to analyze in plain English, and Triagz handles the technical execution across Windows, Linux, and macOS endpoints.</p>
      
      <h2>Comprehensive Malware Analysis Capabilities</h2>
      
      <h3>1. Binary Analysis & Reverse Engineering</h3>
      <p>Triagz provides deep binary analysis capabilities that go beyond traditional static analysis tools:</p>
      
      <h4>Scenario: Investigating a Suspected Banking Trojan</h4>
      <p>A security researcher receives a suspicious executable from an email attachment. Using Triagz, they can quickly:</p>
      <ul>
        <li><strong>Initial Assessment:</strong> Examine PE headers to identify if the file is packed or has unusual characteristics</li>
        <li><strong>Import Analysis:</strong> Review imported functions to understand potential capabilities (network operations, file manipulation, registry access)</li>
        <li><strong>Resource Investigation:</strong> Extract embedded resources that might contain configuration data or additional payloads</li>
        <li><strong>String Analysis:</strong> Identify hardcoded IP addresses, domain names, or cryptocurrency wallet addresses</li>
        <li><strong>Pattern Detection:</strong> Search for credit card validation routines or financial data harvesting patterns</li>
      </ul>
      
      <h4>Scenario: APT Campaign Analysis</h4>
      <p>During incident response, a team discovers multiple suspicious files across their network:</p>
      <ul>
        <li><strong>Batch Analysis:</strong> Scan entire directories for files containing specific hex signatures associated with known APT tools</li>
        <li><strong>Encryption Detection:</strong> Identify files using custom encryption algorithms that might indicate data exfiltration tools</li>
        <li><strong>Configuration Extraction:</strong> Locate and decode embedded C2 server addresses and communication protocols</li>
      </ul>
      
      <h3>2. Threat Intelligence & TTPs</h3>
      
      <h4>Scenario: Ransomware Family Attribution</h4>
      <p>A hospital's IT team discovers encrypted files and needs to identify the ransomware family quickly:</p>
      <ul>
        <li><strong>TTP Mapping:</strong> Automatically extract behavioral patterns and map them to MITRE ATT&CK framework techniques</li>
        <li><strong>Family Classification:</strong> Compare extracted TTPs against known ransomware families to determine attribution</li>
        <li><strong>Threat Hunt Expansion:</strong> Use identified indicators to search for similar threats across the network infrastructure</li>
      </ul>
      
      <h4>Scenario: Custom YARA Rule Deployment</h4>
      <p>A threat intelligence team develops new YARA rules based on emerging threats:</p>
      <ul>
        <li><strong>Rule Validation:</strong> Test custom YARA rules against known sample sets to verify detection accuracy</li>
        <li><strong>System-wide Scanning:</strong> Deploy rules across critical system directories to identify dormant threats</li>
        <li><strong>Continuous Monitoring:</strong> Integrate rules into ongoing monitoring workflows for real-time detection</li>
      </ul>
      
      <h3>3. System Reconnaissance & Environment Analysis</h3>
      <p>Understanding the target environment is crucial for malware analysis. Triagz provides comprehensive system profiling:</p>
      
      <h4>Scenario: Advanced Persistent Threat (APT) Investigation</h4>
      <p>A financial institution suspects an APT group has established persistence in their network:</p>
      <ul>
        <li><strong>Hardware Fingerprinting:</strong> Collect NVRAM variables and BIOS information to identify compromised systems and track attacker infrastructure preferences</li>
        <li><strong>Network Topology Mapping:</strong> Document all network interfaces and configurations to understand potential lateral movement paths</li>
        <li><strong>Disk Forensics:</strong> Analyze partition structures to identify hidden volumes or unusual storage configurations used by attackers</li>
      </ul>
      
      <h4>Scenario: Resource-Based Malware Detection</h4>
      <p>A security operations center notices unusual system performance and needs to identify resource-heavy malware:</p>
      <ul>
        <li><strong>Performance Profiling:</strong> Monitor processes consuming excessive memory or CPU to identify cryptocurrency miners or data exfiltration tools</li>
        <li><strong>System Health Assessment:</strong> Track system uptime patterns and load averages to detect persistence mechanisms</li>
        <li><strong>Process Behavior Analysis:</strong> Examine thread states and command-line arguments to understand malware execution patterns</li>
        <li><strong>Parent-Child Relationships:</strong> Map process trees to identify injection techniques and privilege escalation attempts</li>
      </ul>
      
      <h3>4. Network & Communication Analysis</h3>
      
      <h4>Scenario: Command & Control (C2) Infrastructure Discovery</h4>
      <p>A cybersecurity firm is tracking a sophisticated botnet operation:</p>
      <ul>
        <li><strong>Network Port Mapping:</strong> Identify unusual open ports that might indicate backdoor communications or proxy services</li>
        <li><strong>Certificate Chain Analysis:</strong> Examine SSL/TLS certificates to uncover attacker infrastructure and identify potential domain fronting techniques</li>
        <li><strong>Connection Pattern Analysis:</strong> Monitor network connections to identify beaconing behavior and C2 communication schedules</li>
      </ul>
      
      <h3>5. Digital Forensics & Incident Response</h3>
      
      <h4>Scenario: Data Breach Investigation</h4>
      <p>A retail company discovers unauthorized access to customer data and needs comprehensive forensic analysis:</p>
      <ul>
        <li><strong>Timeline Reconstruction:</strong> Analyze Windows Event Logs to build a chronological timeline of attacker activities</li>
        <li><strong>Persistence Mechanism Detection:</strong> Examine registry keys and startup locations to identify how attackers maintained access</li>
        <li><strong>Privilege Escalation Analysis:</strong> Review kernel modules and drivers to understand how attackers gained elevated privileges</li>
        <li><strong>Data Access Tracking:</strong> Correlate file access logs with user activities to determine the scope of data compromise</li>
      </ul>
      
      <h2>Cross-Platform Support</h2>
      <p>Triagz's unified approach works seamlessly across different operating systems:</p>
      
      <h3>Windows Analysis</h3>
      <ul>
        <li>PE file structure analysis</li>
        <li>Windows Event Log parsing</li>
        <li>Registry examination</li>
        <li>PowerShell script analysis</li>
      </ul>
      
      <h3>Linux Analysis</h3>
      <ul>
        <li>ELF binary analysis</li>
        <li>System call tracing</li>
        <li>Process tree analysis</li>
        <li>Log file correlation</li>
      </ul>
      
      <h3>macOS Analysis</h3>
      <ul>
        <li>Mach-O binary analysis</li>
        <li>System integrity monitoring</li>
        <li>Keychain security analysis</li>
        <li>Application sandbox inspection</li>
      </ul>
      
      <h2>Workflow Integration Scenarios</h2>
      <p>Triagz seamlessly integrates into existing malware analysis workflows through real-world use cases:</p>
      
      <h3>Scenario: SOC Alert Triage</h3>
      <p>When a SOC analyst receives an alert about suspicious file execution, they can use Triagz to rapidly determine threat severity and scope within minutes rather than hours of manual analysis.</p>
      
      <h3>Scenario: Incident Response Investigation</h3>
      <p>During a security incident, response teams can leverage Triagz to quickly extract behavioral indicators, map attack techniques to MITRE ATT&CK, and identify persistence mechanisms across affected systems.</p>
      
      <h3>Scenario: Proactive Threat Hunting</h3>
      <p>Threat hunters can deploy custom YARA rules developed from recent threat intelligence to scan enterprise environments, identifying dormant threats that traditional signature-based tools might miss.</p>
      
      <h3>Scenario: Forensic Case Documentation</h3>
      <p>Digital forensics teams can use Triagz's natural language interface to document their analysis process, making it easier to explain technical findings to non-technical stakeholders in legal proceedings.</p>
      
      <h2>Real-World Impact</h2>
      <p>Security teams using Triagz have reported significant improvements in their analysis workflows:</p>
      
      <blockquote>
        "Triagz reduced our initial triage time from 2 hours to 15 minutes. The natural language interface means our junior analysts can perform complex analysis tasks without extensive training." - Senior Malware Analyst, Fortune 500 Company
      </blockquote>
      
      <blockquote>
        "The cross-platform support is game-changing. We can analyze Windows, Linux, and macOS threats using the same interface and methodology." - Security Research Team Lead
      </blockquote>
      
      <h2>Getting Started with Triagz</h2>
      <p>Begin your malware analysis journey with Triagz:</p>
      
      <ol>
        <li><strong>Profile Setup:</strong> Create analysis profiles for your different environments (Windows, Linux, macOS)</li>
        <li><strong>Agent Deployment:</strong> Install Triagz agents on your analysis machines using the provided agent keys</li>
        <li><strong>Start Simple:</strong> Begin with basic file enumeration and system profiling to familiarize yourself with the interface</li>
        <li><strong>Expand Analysis:</strong> Progress to complex analysis like TTP extraction, YARA rule execution, and behavioral analysis</li>
        <li><strong>Automate Workflows:</strong> Develop standardized analysis patterns for consistent investigative results</li>
      </ol>
      
      <h2>The Future of Malware Analysis</h2>
      <p>Triagz represents the evolution of malware analysis from a tool-centric approach to an intelligence-driven methodology. By combining the analytical power of AI with the intuitive nature of natural language, Triagz empowers researchers to focus on what they do best: understanding threats and protecting systems.</p>
      
      <p>Whether you're conducting initial triage, performing deep binary analysis, or hunting for advanced persistent threats, Triagz provides the comprehensive capabilities needed to stay ahead of evolving cyber threats.</p>
    `,
    author: "Security Research Team",
    date: "2024-01-15",
    category: "Malware Analysis",
    tags: ["AI", "Malware", "Analysis", "DeepSeek"],
    readTime: "8 min read",
    featured: false,
    image: "/api/placeholder/800/400"
  },
  {
    id: 2,
    title: "Cross-Platform Endpoint Security: Windows, Linux, and macOS Best Practices",
    excerpt: "Learn the essential techniques for securing endpoints across different operating systems and how triagz's unified approach simplifies multi-platform security research.",
    content: `
      <h2>Introduction</h2>
      <p>In today's diverse computing environment, security teams must protect endpoints across multiple operating systems. Each platform presents unique challenges and opportunities for both defenders and attackers.</p>
      
      <h2>Windows Security Considerations</h2>
      <p>Windows environments require special attention to:</p>
      <ul>
        <li>Registry monitoring and analysis</li>
        <li>PowerShell script execution</li>
        <li>Windows Event Log analysis</li>
        <li>PE file structure examination</li>
      </ul>
      
      <h2>Linux Security Best Practices</h2>
      <p>Linux systems benefit from:</p>
      <ul>
        <li>Process monitoring and analysis</li>
        <li>System call tracing</li>
        <li>ELF binary analysis</li>
        <li>Log file correlation</li>
      </ul>
      
      <h2>macOS Security Focus Areas</h2>
      <p>macOS security requires attention to:</p>
      <ul>
        <li>Mach-O binary analysis</li>
        <li>System integrity protection</li>
        <li>Keychain security</li>
        <li>Application sandbox monitoring</li>
      </ul>
      
      <h2>triagz's Unified Approach</h2>
      <p>triagz eliminates the complexity of managing multiple platform-specific tools by providing a unified interface for cross-platform security research.</p>
    `,
    author: "DevOps Team",
    date: "2024-01-10",
    category: "Security",
    tags: ["Cross-Platform", "Endpoint", "Security", "Best Practices"],
    readTime: "6 min read",
    featured: false,
    image: "/api/placeholder/800/400"
  },
  {
    id: 7,
    title: "Complete Installation Guide: Setting up Triagz Agents on Windows, Linux, and macOS",
    excerpt: "Step-by-step instructions for installing and configuring Triagz agents across all three major operating systems. From Windows MSI packages to Linux binaries and macOS applications, get your endpoints connected quickly and securely.",
    content: `
      <h2>Introduction</h2>
      <p>Welcome to the comprehensive Triagz Agent installation guide! This step-by-step tutorial will walk you through installing and configuring Triagz agents on Windows, Linux, and macOS systems. Whether you're a security researcher, IT administrator, or cybersecurity professional, this guide will help you get your endpoints connected to the Triagz platform quickly and securely.</p>
      
      <p>Triagz agents enable you to perform AI-powered security analysis and malware investigation across your infrastructure using natural language commands. Each platform offers a tailored installation experience while maintaining consistent functionality.</p>

      <h2>Prerequisites</h2>
      <p>Before installing any Triagz agent, ensure you have:</p>
      <ul>
        <li>An active Triagz account at <a href="https://triagz.com" target="_blank">triagz.com</a></li>
        <li>Administrative privileges on the target machine</li>
        <li>A stable internet connection</li>
        <li>The ability to create firewall exceptions if needed</li>
      </ul>

      <h2>Windows Installation</h2>
      <p>The Windows agent comes as an MSI installer package that provides a user-friendly GUI application.</p>

      <h3>Download and Installation</h3>
      <ol>
        <li><strong>Download the MSI package</strong> from the Triagz downloads page</li>
        <li><strong>Important Security Note:</strong> Since the MSI is not digitally signed by a trusted certificate authority, Windows may display a security warning. You may need to allow installation of untrusted applications by clicking "More info" → "Run anyway" in the Windows Security prompt.</li>
        <li><strong>Run the installer</strong> with administrator privileges</li>
        <li><strong>Follow the installation wizard</strong> - the application will be installed to <code>C:\\Program Files\\Triagz\\Agent\\</code></li>
      </ol>

      <h3>First Launch and Setup</h3>
      <p>After installation, launch the Triagz Agent from the Start Menu or desktop shortcut.</p>

      <p>The application will open with a clean, professional interface showing:</p>
      <ul>
        <li><strong>Agent Key section</strong> - For generating and managing your unique agent identifier</li>
        <li><strong>Connection status</strong> - Shows current connection state to the Triagz server</li>
        <li><strong>Activity log</strong> - Real-time logging of agent operations</li>
        <li><strong>Control buttons</strong> - Generate key, connect/disconnect, and settings</li>
      </ul>

      <h3>Windows-Specific Features</h3>
      <ul>
        <li><strong>System Tray Integration:</strong> Minimize the application to run in the background</li>
        <li><strong>Auto-start on Boot:</strong> Enable automatic startup with Windows</li>
        <li><strong>Registry Storage:</strong> Agent keys are securely stored in the Windows registry</li>
        <li><strong>Windows Service Mode:</strong> Can run as a Windows service for enterprise deployments</li>
      </ul>

      <h2>Linux Installation</h2>
      <p>The Linux agent comes with an automated installation script that handles the complete setup process.</p>

      <h3>Download and Installation</h3>
      <ol>
        <li><strong>Download the Linux package</strong> containing the binary and installation script</li>
        <li><strong>Extract the package:</strong>
          <pre><code>unzip triagz-agent-linux.zip
cd triagz-agent-linux</code></pre>
        </li>
        <li><strong>Run the installation script as root:</strong>
          <pre><code>sudo ./install.sh</code></pre>
        </li>
      </ol>

      <h3>What the Installation Script Does</h3>
      <p>The <code>install.sh</code> script automatically:</p>
      <ul>
        <li>Verifies administrative privileges</li>
        <li>Makes the binary executable</li>
        <li>Copies <code>triagz-agent</code> to <code>/usr/bin/</code></li>
        <li>Creates system directories (<code>/etc/triagz</code>, <code>/var/log/triagz</code>)</li>
        <li>Sets proper permissions</li>
        <li>Provides usage instructions</li>
      </ul>

      <h3>System Dependencies (If Needed)</h3>
      <p>Most modern Linux distributions include the required libraries. If you encounter issues, install these dependencies:</p>
      
      <h4>Ubuntu/Debian:</h4>
      <pre><code>sudo apt-get update
sudo apt-get install -y libmagic1 build-essential python3-dev</code></pre>

      <h4>CentOS/RHEL/Fedora:</h4>
      <pre><code># CentOS/RHEL
sudo yum install -y file-libs gcc python3-devel

# Fedora  
sudo dnf install -y file-libs gcc python3-devel</code></pre>

      <h3>Configuration and Usage</h3>
      <p>The Linux agent uses a command-line interface with several key operations:</p>

      <h4>Generate Agent ID (One-time setup):</h4>
      <pre><code>triagz-agent --generate</code></pre>
      <p>This creates a unique agent identifier saved to <code>/etc/triagz/agent_id</code> or <code>~/.triagz/agent_id</code></p>

      <h4>Connect to Server:</h4>
      <pre><code># Daemon mode (background, recommended for production)
triagz-agent --connect

# Foreground mode (for debugging)
triagz-agent --connect --foreground</code></pre>

      <h4>View Configuration:</h4>
      <pre><code>triagz-agent --info</code></pre>

      <h2>macOS Installation</h2>
      <p>The macOS agent comes as a native Mac application distributed in a DMG installer.</p>

      <h3>Download and Install</h3>
      <ol>
        <li><strong>Download the DMG file:</strong> <code>TriagzAgent-Installer-v1.0.1.dmg</code></li>
        <li><strong>Mount the DMG</strong> by double-clicking it</li>
        <li><strong>Drag TriagzAgent.app</strong> to the Applications folder</li>
        <li><strong>Eject the DMG</strong> when installation is complete</li>
      </ol>

      <h3>Security Configuration (Important!)</h3>
      <p>Since the macOS application is not code-signed by Apple, you'll need to remove the quarantine attribute before first launch:</p>
      
      <pre><code>xattr -rd com.apple.quarantine /Applications/TriagzAgent.app</code></pre>
      
      <p><strong>Note:</strong> This command removes the macOS quarantine flag that prevents unsigned applications from running. Only run this command on software you trust.</p>

      <h3>First Launch</h3>
      <p>After running the quarantine removal command, launch TriagzAgent from your Applications folder:</p>
      <ol>
        <li>macOS may still show a security dialog - click "Open" to proceed</li>
        <li>The application will open with a native macOS interface</li>
        <li>You'll see options to generate keys, connect to the server, and configure auto-start</li>
      </ol>

      <h3>macOS-Specific Features</h3>
      <ul>
        <li><strong>Menu Bar Integration:</strong> Access controls from the macOS menu bar</li>
        <li><strong>Launch Agent Support:</strong> Automatic startup via macOS Launch Agents</li>
        <li><strong>Native UI:</strong> Follows macOS Human Interface Guidelines</li>
        <li><strong>Keychain Integration:</strong> Secure storage using macOS keychain services</li>
      </ul>

      <h2>Connection Setup (All Platforms)</h2>
      <p>After installing the agent on any platform, follow these steps to establish a connection with the Triagz server:</p>

      <h3>Step 1: Generate Agent Key</h3>
      <p>Each agent requires a unique identifier to connect to your Triagz profile:</p>
      <ul>
        <li><strong>Windows/macOS:</strong> Click the "Generate New Key" button in the GUI</li>
        <li><strong>Linux:</strong> Run <code>triagz-agent --generate</code></li>
      </ul>
      <p><strong>Important:</strong> Save this key securely - it's a one-time generated identifier that cannot be recovered if lost.</p>

      <h3>Step 2: Create Profile on Triagz.com</h3>
      <ol>
        <li><strong>Log in</strong> to your Triagz account at <a href="https://triagz.com" target="_blank">triagz.com</a></li>
        <li><strong>Navigate to Profiles</strong> - Access your profile management dashboard</li>
        <li><strong>Create New Profile</strong> - Click "Add New Profile" or similar button</li>
        <li><strong>Enter Profile Details:</strong>
          <ul>
            <li>Profile name (e.g., "Development Machine", "Production Server")</li>
            <li>Description (optional)</li>
            <li>Platform type (Windows/Linux/macOS)</li>
          </ul>
        </li>
        <li><strong>Add Agent Key</strong> - Paste the agent key generated in Step 1</li>
        <li><strong>Save Profile</strong> - Confirm the profile creation</li>
      </ol>

      <h3>Step 3: Connect Agent to Server</h3>
      <p>With the profile created and agent key registered:</p>
      <ul>
        <li><strong>Windows/macOS:</strong> Click the "Connect" button in the agent application</li>
        <li><strong>Linux:</strong> Run <code>triagz-agent --connect</code></li>
      </ul>

      <p>The agent will attempt to establish a WebSocket connection to the Triagz servers. You should see:</p>
      <ul>
        <li>Connection status change to "Connected" or "Online"</li>
        <li>Activity logs showing successful authentication</li>
        <li>The agent appearing as "Active" in your Triagz web dashboard</li>
      </ul>

      <h2>Troubleshooting Common Issues</h2>

      <h3>Connection Problems</h3>
      <ul>
        <li><strong>Firewall blocking connection:</strong> Ensure outbound HTTPS (port 443) is allowed</li>
        <li><strong>Invalid agent key:</strong> Verify the key was copied correctly and is registered in your profile</li>
        <li><strong>Network connectivity:</strong> Test connection to triagz.com manually</li>
      </ul>

      <h3>Windows-Specific Issues</h3>
      <ul>
        <li><strong>MSI won't install:</strong> Right-click installer and select "Run as administrator"</li>
        <li><strong>Antivirus blocking:</strong> Add Triagz agent to antivirus exceptions</li>
        <li><strong>Windows Defender SmartScreen:</strong> Click "More info" then "Run anyway"</li>
      </ul>

      <h3>Linux-Specific Issues</h3>
      <ul>
        <li><strong>Permission denied:</strong> Ensure binary is executable with <code>chmod +x</code></li>
        <li><strong>Missing libraries:</strong> Install system dependencies as shown above</li>
        <li><strong>Path not found:</strong> Verify <code>/usr/local/bin</code> is in your PATH</li>
      </ul>

      <h3>macOS-Specific Issues</h3>
      <ul>
        <li><strong>"App cannot be opened":</strong> Run the quarantine removal command</li>
        <li><strong>Gatekeeper warnings:</strong> Go to System Preferences > Security & Privacy and click "Open Anyway"</li>
        <li><strong>Auto-start not working:</strong> Check System Preferences > Users & Groups > Login Items</li>
      </ul>

      <h2>Next Steps</h2>
      <p>Once your agents are successfully connected:</p>
      <ol>
        <li><strong>Test the connection</strong> by running a simple command like "list running processes"</li>
        <li><strong>Explore capabilities</strong> by asking natural language questions about your system</li>
        <li><strong>Configure monitoring</strong> for critical systems and processes</li>
        <li><strong>Set up automated scans</strong> for malware detection and analysis</li>
        <li><strong>Review logs regularly</strong> to ensure agents are functioning properly</li>
      </ol>

      <h2>Security Best Practices</h2>
      <ul>
        <li><strong>Keep agent keys secure</strong> - treat them like passwords</li>
        <li><strong>Use HTTPS connections</strong> - never disable SSL in production</li>
        <li><strong>Regular updates</strong> - keep agents updated with the latest security patches</li>
        <li><strong>Monitor access logs</strong> - review connection logs periodically</li>
        <li><strong>Network segmentation</strong> - consider isolating agent traffic where appropriate</li>
      </ul>

      <h2>Getting Support</h2>
      <p>If you encounter issues during installation or setup:</p>
      <ul>
        <li>Check the <strong>troubleshooting section</strong> above for common solutions</li>
        <li>Review <strong>agent logs</strong> for detailed error messages</li>
        <li>Visit the <strong>Triagz documentation</strong> at triagz.com/docs</li>
        <li>Contact <strong>support</strong> through your Triagz dashboard</li>
        <li>Join the <strong>community forums</strong> for peer assistance</li>
      </ul>

      <p>Welcome to the Triagz platform! Your agents are now ready to help you conduct advanced security analysis and malware research using the power of artificial intelligence.</p>
    `,
    author: "Triagz Development Team",
    date: "2024-02-01",
    category: "Installation",
    tags: ["Installation", "Windows", "Linux", "macOS", "Setup", "Agent", "Cross-Platform"],
    readTime: "15 min read",
    featured: true,
    image: "/api/placeholder/800/400"
  },
];

function BlogPost() {
  const { id } = useParams();
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const post = blogPosts.find(p => p.id === parseInt(id));

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!post) {
    return (
      <div className="blogpost-container">
        <div className="blogpost-ambient-bg">
          <div className="ambient-orb ambient-orb-1"></div>
        </div>
        <header className="blogpost-header scrolled">
          <div className="header-content">
            <Link to="/" className="header-logo">
              <Logo size="small" showText={false} />
            </Link>
            <Link to="/blog" className="back-to-blog-btn">
              <FaArrowLeft />
              Back to Blog
            </Link>
          </div>
        </header>
        <main className="blogpost-main">
          <div className="container">
            <div className="not-found">
              <div className="not-found-icon">
                <FaBookmark />
              </div>
              <h1>Article Not Found</h1>
              <p>The article you're looking for doesn't exist or has been moved.</p>
              <Link to="/blog" className="back-btn">
                <FaArrowLeft />
                Back to Blog
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Malware Analysis": return <FaBug />;
      case "Security": return <FaShieldAlt />;
      case "Threat Detection": return <FaEye />;
      case "Development": return <FaCode />;
      case "Incident Response": return <FaRocket />;
      case "Philosophy": return <FaLightbulb />;
      case "Installation": return <FaCog />;
      default: return <FaTag />;
    }
  };

  const shareUrl = window.location.href;
  const shareTitle = post.title;

  // Get related posts
  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 2);

  return (
    <div className="blogpost-container">
      {/* Ambient Background */}
      <div className="blogpost-ambient-bg">
        <div className="ambient-orb ambient-orb-1"></div>
        <div className="ambient-orb ambient-orb-2"></div>
      </div>

      {/* Header */}
      <header className={`blogpost-header ${isHeaderScrolled ? 'scrolled' : ''}`}>
        <div className="header-content">
          <Link to="/" className="header-logo">
            <Logo size="small" showText={false} />
          </Link>
          <div className="header-actions">
            <Link to="/blog" className="back-to-blog-btn">
              <FaArrowLeft />
              <span>Back to Blog</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="blogpost-main">
        {/* Article Hero */}
        <article className="article-hero">
          <div className="hero-decoration">
            <div className="deco-grid"></div>
          </div>
          <div className="container">
            <div className="article-meta">
              <span className="category-badge">
                {getCategoryIcon(post.category)}
                {post.category}
              </span>
              {post.featured && (
                <span className="featured-badge">
                  <FaBookmark />
                  Featured
                </span>
              )}
            </div>
            
            <h1>{post.title}</h1>
            
            <p className="article-excerpt">{post.excerpt}</p>
            
            <div className="article-info">
              <div className="author-block">
                <div className="author-avatar">
                  <FaUser />
                </div>
                <div className="author-details">
                  <span className="author-name">{post.author}</span>
                  <div className="meta-row">
                    <span>
                      <FaCalendarAlt />
                      {new Date(post.date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                    <span>
                      <FaClock />
                      {post.readTime}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="article-tags">
                {post.tags.map(tag => (
                  <span key={tag} className="tag">#{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Article Content */}
        <div className="article-content-wrapper">
          <div className="container">
            <div className="article-layout">
              <div className="article-body">
                <div 
                  className="content-body" 
                  dangerouslySetInnerHTML={{ __html: post.content }} 
                />
              </div>

              {/* Sidebar */}
              <aside className="article-sidebar">
                <div className="sidebar-card share-card">
                  <h4>
                    <FaShare />
                    Share Article
                  </h4>
                  <div className="share-buttons">
                    <a 
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn twitter"
                      title="Share on Twitter"
                    >
                      <FaTwitter />
                    </a>
                    <a 
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn linkedin"
                      title="Share on LinkedIn"
                    >
                      <FaLinkedin />
                    </a>
                    <button 
                      className={`share-btn copy ${copied ? 'copied' : ''}`}
                      onClick={copyToClipboard}
                      title="Copy link"
                    >
                      {copied ? <FaCheck /> : <FaCopy />}
                    </button>
                  </div>
                </div>

                <div className="sidebar-card tags-card">
                  <h4>
                    <FaTag />
                    Tags
                  </h4>
                  <div className="sidebar-tags">
                    {post.tags.map(tag => (
                      <span key={tag} className="sidebar-tag">#{tag}</span>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="related-section">
            <div className="container">
              <h3 className="related-title">Related Articles</h3>
              <div className="related-grid">
                {relatedPosts.map(relatedPost => (
                  <Link 
                    key={relatedPost.id} 
                    to={`/blog/${relatedPost.id}`} 
                    className="related-card"
                  >
                    <div className="related-card-content">
                      <div className="related-meta">
                        <span className="related-category">
                          {getCategoryIcon(relatedPost.category)}
                          {relatedPost.category}
                        </span>
                        <span className="related-time">
                          <FaClock />
                          {relatedPost.readTime}
                        </span>
                      </div>
                      <h4>{relatedPost.title}</h4>
                      <p>{relatedPost.excerpt.substring(0, 120)}...</p>
                      <span className="read-link">
                        Read Article
                        <FaChevronRight />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default BlogPost;
